import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@contexts/LanguageContext';
import { StatusBadge } from '@components/common';
import { useNotification } from '@hooks/useNotification';
import api from '@services/api';
import './PendingValidationsPage.scss';

export const PendingValidationsPage = () => {
    const { t, currentLanguage } = useLanguage();
    const navigate = useNavigate();
    const notify = useNotification();

    const [pendingBorrowings, setPendingBorrowings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [validatingId, setValidatingId] = useState(null);

    const fetchPendingValidations = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.borrowings.getPendingValidations();
            setPendingBorrowings(response.data?.models || response.data || []);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch pending validations:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPendingValidations();
    }, [fetchPendingValidations]);

    const handleValidate = async (borrowingId, status) => {
        try {
            setValidatingId(borrowingId);
            await api.borrowings.validate(borrowingId, { status });

            const msgKey = status === 'validated'
                ? 'borrowing.validation.validated'
                : 'borrowing.validation.rejected';
            notify.success(t(msgKey));

            // Refresh the list
            await fetchPendingValidations();
        } catch (err) {
            console.error('Failed to validate:', err);
            notify.error(t('borrowing.validation.error'));
        } finally {
            setValidatingId(null);
        }
    };

    const handleViewBorrowing = (borrowingId) => {
        navigate(`/borrowings/${borrowingId}`);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString(currentLanguage, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="pending-validations-page">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>{t('common.loading')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="pending-validations-page">
                <div className="error-container">
                    <p>{error}</p>
                    <button onClick={fetchPendingValidations} className="btn btn-primary">
                        {t('common.retry') || 'Retry'}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="pending-validations-page">
            {pendingBorrowings.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">✅</div>
                    <h3>{t('borrowing.validation.noPending') || 'No pending validations'}</h3>
                    <p>{t('borrowing.validation.noPendingDescription') || 'All reservations have been processed.'}</p>
                </div>
            ) : (
                <div className="validations-list">
                    {pendingBorrowings.map(borrowing => (
                        <div key={borrowing.id} className="validation-card">
                            <div className="validation-card__header">
                                <div className="validation-card__resource" onClick={() => handleViewBorrowing(borrowing.id)}>
                                    <h4>{borrowing.resource?.name || t('borrowing.unknown_resource')}</h4>
                                </div>
                                <StatusBadge model={borrowing} type="borrowing" className="size-small" />
                            </div>

                            <div className="validation-card__details">
                                <div className="validation-card__info">
                                    <span className="label">{t('borrowing.borrower')}:</span>
                                    <span className="value">{borrowing.user?.name || borrowing.user?.email || t('borrowing.unknown_user')}</span>
                                </div>
                                <div className="validation-card__info">
                                    <span className="label">{t('borrowingView.reservedFromTo')}:</span>
                                    <span className="value">
                                        {formatDate(borrowing.reserved_from)} - {formatDate(borrowing.reserved_until)}
                                    </span>
                                </div>
                                {borrowing.reservation_notes && (
                                    <div className="validation-card__info">
                                        <span className="label">{t('borrowing.notes')}:</span>
                                        <span className="value">{borrowing.reservation_notes}</span>
                                    </div>
                                )}
                            </div>

                            <div className="validation-card__actions">
                                <button
                                    className="btn btn-success btn-sm"
                                    onClick={() => handleValidate(borrowing.id, 'validated')}
                                    disabled={validatingId === borrowing.id}
                                >
                                    {t('borrowing.validation.approve')}
                                </button>
                                <button
                                    className="btn btn-danger btn-sm"
                                    onClick={() => handleValidate(borrowing.id, 'rejected')}
                                    disabled={validatingId === borrowing.id}
                                >
                                    {t('borrowing.validation.reject')}
                                </button>
                                <button
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => handleViewBorrowing(borrowing.id)}
                                >
                                    {t('borrowing.details')}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PendingValidationsPage;
